import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import ButtonBase from "@mui/material/ButtonBase";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { api, get, post, put } from "lib/api";
import {
  extractStrapiUploadFileId,
  isValidStrapiUploadFileId,
} from "utils/strapiUploadIds";
import MobileTopBar from "mobile/components/MobileTopBar";
import MobileImageViewer from "mobile/components/MobileImageViewer";
import { IosCard, IosFilterBar, IosPage, iosColors } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString();
  } catch {
    return String(v).slice(0, 10);
  }
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
};

const computeStageStatus = (stageStatus) => {
  const s = String(stageStatus || "").toLowerCase();
  if (s === "sold") return { label: "Sold", color: "success" };
  if (s === "warranty") return { label: "Review", color: "warning" };
  if (s === "not_started") return { label: "Not started", color: "default" };
  if (s === "passed" || s === "complete" || s === "completed") return { label: "Completed", color: "success" };
  if (s === "in_progress") return { label: "In progress", color: "info" };
  if (s === "blocked" || s === "failed") return { label: "In progress", color: "info" };
  if (s === "pending") return { label: "Not started", color: "default" };
  return { label: "Not started", color: "default" };
};

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  py: 1,
  fontWeight: 900,
  lineHeight: 1.15,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  textAlign: "center",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
  "& .MuiButton-startIcon": { flexShrink: 0 },
  "&.Mui-disabled": { bgcolor: "rgba(181,71,8,0.18)", color: "rgba(15,15,18,0.44)" },
};

const secondaryActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 44,
  py: 1,
  fontWeight: 900,
  lineHeight: 1.15,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  textAlign: "center",
  textTransform: "none",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)", borderColor: "rgba(181,71,8,0.52)" },
  "& .MuiButton-startIcon": { flexShrink: 0 },
  "&.Mui-disabled": { borderColor: "rgba(181,71,8,0.15)", color: "rgba(15,15,18,0.36)" },
};

const visibleTextSx = {
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  whiteSpace: "normal",
};

const normalizeFile = (f) => {
  if (!f) return null;
  if (isValidStrapiUploadFileId(f)) return { id: f };
  if (f?.data) return f.data?.attributes ? { id: f.data.id, ...f.data.attributes } : { ...f.data };
  if (f?.attributes) return { id: f.id, ...f.attributes };
  return f;
};

const getMediaId = (file) => extractStrapiUploadFileId(file);

const getFileUrl = (file, baseURL) => {
  const f = normalizeFile(file);
  const urlPath = f?.url;
  if (!urlPath) return null;
  if (/^https?:\/\//i.test(urlPath)) return urlPath;
  return `${baseURL || ""}${urlPath}`;
};

const getFileName = (file) => normalizeFile(file)?.name || null;

const getFileType = (file) => {
  const f = normalizeFile(file);
  if (!f) return null;
  if (f.mime) return String(f.mime);
  if (f.ext) return String(f.ext);
  if (f.name && String(f.name).includes(".")) return String(f.name).split(".").pop();
  return null;
};

const normalizeChecklist = (checklist) => {
  if (!checklist) return [];
  return (Array.isArray(checklist) ? checklist : [checklist])
    .map((it) => {
      const d = it?.attributes || it;
      return { text: d?.text || "", required: d?.required === true || d?.required === "true" };
    })
    .filter((x) => x.text && x.text.trim() !== "");
};

const normalizeStep = (step) => {
  const d = step?.attributes || step;
  const images = d?.images?.data ? asArray(d.images.data) : asArray(d?.images);
  const documents = d?.documents?.data ? asArray(d.documents.data) : asArray(d?.documents);
  return {
    ...d,
    images,
    documents,
    checklist: normalizeChecklist(d?.checklist),
    imageAnnotations: d?.imageAnnotations || d?.image_annotations || { version: 1, images: {} },
  };
};

const normalizeSubSteps = (subSteps) => {
  const arr = Array.isArray(subSteps) ? subSteps : [];
  return arr
    .map((s) => {
      const d = s?.attributes || s;
      const images = d?.images?.data ? asArray(d.images.data) : asArray(d?.images);
      const documents = d?.documents?.data ? asArray(d.documents.data) : asArray(d?.documents);
      return {
        id: d?.id,
        title: d?.title || "",
        body: d?.body || "",
        images,
        documents,
        imageAnnotations: d?.imageAnnotations || d?.image_annotations || { version: 1, images: {} },
      };
    })
    .filter(Boolean);
};

const MEDIA_FIELD_KEYS = new Set([
  "image",
  "images",
  "document",
  "documents",
  "attachment",
  "attachments",
  "photo",
  "photos",
  "referenceImage",
  "referenceImages",
  "uploadedImage",
  "uploadedImages",
  "file",
  "files",
]);

const looksLikeMediaObject = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      (value.url || value.mime || value.ext || value.formats || value.provider || value.hash || value.alternativeText)
  );

const collectNumericMediaIds = (obj) => {
  const out = new Set();
  const addId = (value) => {
    if (isValidStrapiUploadFileId(value)) out.add(Number(value));
  };
  const visit = (v, mode = "scan") => {
    if (!v) return;
    if (typeof v === "number" || typeof v === "string") {
      if (mode === "media") addId(v);
      return;
    }
    if (Array.isArray(v)) {
      v.forEach((item) => visit(item, mode));
      return;
    }
    if (typeof v === "object") {
      const isMediaObject = looksLikeMediaObject(v);
      if (mode === "media" || isMediaObject) addId(v.id);
      if (v?.data) visit(v.data, mode === "media" || isMediaObject ? "media" : "scan");
      if (v?.attributes) visit(v.attributes, mode === "media" || isMediaObject ? "media" : "scan");
      Object.entries(v).forEach(([key, value]) => {
        if (key === "id" || key === "attributes" || key === "data") return;
        if (MEDIA_FIELD_KEYS.has(key)) {
          visit(value, "media");
        } else if (mode === "scan") {
          visit(value, "scan");
        }
      });
    }
  };
  visit(obj, "scan");
  return Array.from(out);
};

export default function ValveBodyDetailMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [viewer, setViewer] = useState({ open: false, file: null, title: "", annotations: [] });
  const [tab, setTab] = useState(0);
  const [expandedStep, setExpandedStep] = useState(false);
  const [stepChecklistState, setStepChecklistState] = useState({});
  const [actionNotice, setActionNotice] = useState(null);
  const [partsBankTemplateId, setPartsBankTemplateId] = useState("");
  const [partsBankSearch, setPartsBankSearch] = useState("");
  const [partsBankQtyByLineKey, setPartsBankQtyByLineKey] = useState({});
  const [testEntryValues, setTestEntryValues] = useState({});
  const [qualityCheckValues, setQualityCheckValues] = useState({});

  const baseURL = api?.defaults?.baseURL || "";

  const { data, isLoading } = useQuery({
    queryKey: ["m-valve-body", documentId],
    queryFn: () =>
      get(`/api/valve-bodies/${documentId}`, {
        populate: {
          workOrder: true,
          workOrderItem: {
            populate: {
              buildSheet: {
                populate: {
                  bulkLines: { populate: { inventoryItem: true } },
                  coreItem: true,
                  steps: { populate: { images: true, documents: true, checklist: true } },
                  requiredTests: { populate: { testEntries: { populate: { images: true } } } },
                  qualityChecks: { populate: { images: true } },
                  identifiers: { populate: { entries: true } },
                  additionalInformation: { populate: { documents: true } },
                },
              },
            },
          },
          finishedGood: true,
          photos: true,
          coreReturns: { populate: { invoice: true } },
        },
      }),
    enabled: !!documentId,
    staleTime: 5_000,
  });

  const unit = useMemo(() => {
    const raw = data?.data || data;
    return raw ? normalize(raw) : null;
  }, [data]);

  const displayTag = unit?.optionalTag || unit?.serialNumber || "Valve Body";
  const wo = unit?.workOrder?.data ? normalize(unit.workOrder.data) : unit?.workOrder ? normalize(unit.workOrder) : null;
  const fg = unit?.finishedGood?.data ? normalize(unit.finishedGood.data) : unit?.finishedGood ? normalize(unit.finishedGood) : null;
  const line = unit?.workOrderItem?.data ? normalize(unit.workOrderItem.data) : unit?.workOrderItem ? normalize(unit.workOrderItem) : null;
  const buildSheet = line?.buildSheet?.data ? normalize(line.buildSheet.data) : line?.buildSheet ? normalize(line.buildSheet) : null;

  const unitKey = String(unit?.documentId || unit?.id || "");

  const { data: stageRecordData } = useQuery({
    queryKey: ["m-unit-latest-stage-record", unitKey],
    queryFn: async () => {
      if (!unitKey) return null;
      const isNumeric = Number.isFinite(Number(unitKey));
      const filters = isNumeric ? { unit: { id: { $eq: Number(unitKey) } } } : { unit: { documentId: { $eq: unitKey } } };
      const res = await get("/api/stage-records", {
        publicationState: "preview",
        populate: {
          unit: true,
          stage: true,
          attachments: true,
          testResults: true,
          technicianNotes: true,
          identifierValues: { populate: { images: true } },
          qualityCheckValues: { populate: { images: true } },
        },
        sort: "createdAt:desc",
        filters,
        pagination: { page: 1, pageSize: 1 },
      });
      const first = Array.isArray(res?.data) ? res.data[0] : null;
      return first ? normalize(first) : null;
    },
    enabled: !!unitKey,
    staleTime: 5_000,
  });

  const stageChip = computeStageStatus(stageRecordData?.status);
  const sectionOptions = useMemo(
    () => [
      { key: 0, label: "Overview", icon: "dashboard", accent: "orange" },
      { key: 1, label: "Steps", icon: "format_list_numbered", accent: "green" },
      { key: 2, label: "Tests", icon: "science", accent: "orange" },
      { key: 3, label: "QC", icon: "verified", accent: "green" },
      { key: 7, label: "Materials", icon: "precision_manufacturing", accent: "orange" },
      { key: 8, label: "Parts Bank", icon: "shopping_cart", accent: "green" },
      { key: 5, label: "IDs", icon: "fingerprint", accent: "orange" },
      { key: 4, label: "Info", icon: "folder_open", accent: "green" },
    ],
    []
  );

  const photos = useMemo(() => asArray(unit?.photos?.data || unit?.photos), [unit]);
  const coreReturns = useMemo(() => asArray(unit?.coreReturns?.data || unit?.coreReturns), [unit]);

  const normalizedSteps = useMemo(() => asArray(buildSheet?.steps).map(normalizeStep), [buildSheet?.steps]);

  const normalizedTests = useMemo(() => {
    const tests = asArray(buildSheet?.requiredTests);
    return tests.map((t) => {
      const d = t?.attributes || t;
      const testEntries = asArray(d?.testEntries).map((e) => {
        const ed = e?.attributes || e;
        const images = ed?.images?.data ? asArray(ed.images.data) : asArray(ed?.images);
        return { ...ed, images };
      });
      return { testName: d?.testName || d?.name || "", testEntries };
    });
  }, [buildSheet?.requiredTests]);

  const normalizedQualityChecks = useMemo(() => {
    const checks = asArray(buildSheet?.qualityChecks);
    return checks.map((qc) => {
      const d = qc?.attributes || qc;
      const images = d?.images?.data ? asArray(d.images.data) : asArray(d?.images);
      return { ...d, images };
    });
  }, [buildSheet?.qualityChecks]);

  const normalizedAdditionalInfo = useMemo(() => {
    const info = asArray(buildSheet?.additionalInformation);
    return info.map((ai) => {
      const d = ai?.attributes || ai;
      const documents = d?.documents?.data ? asArray(d.documents.data) : asArray(d?.documents);
      return { name: d?.name || "", documents };
    });
  }, [buildSheet?.additionalInformation]);

  const coreItemRel = buildSheet?.coreItem?.data || buildSheet?.coreItem || null;
  const coreItem = coreItemRel?.attributes ? { id: coreItemRel.id, documentId: coreItemRel.documentId || coreItemRel.id, ...coreItemRel.attributes } : coreItemRel;
  const coreItemNumericIdForTemplate = Number.isFinite(Number(coreItem?.id)) && Number(coreItem?.id) > 0 ? Number(coreItem.id) : null;
  const coreItemDocumentIdForTemplate = coreItem?.documentId ? String(coreItem.documentId).trim() : "";
  const coreItemTemplateRefKey = coreItemDocumentIdForTemplate || String(coreItemNumericIdForTemplate || "");
  const unitRefForPartsBank = unit ? String(unit.id || unit.documentId || "") : "";
  const unitRefForComponentOrder = unit ? String(unit.documentId || unit.id || "") : "";

  const normalizedBulkLines = useMemo(() => {
    const raw = buildSheet?.bulkLines?.data || buildSheet?.bulkLines || [];
    return (Array.isArray(raw) ? raw : [])
      .map((lineRow) => {
        const row = lineRow?.attributes || lineRow;
        const inv = row?.inventoryItem?.data || row?.inventoryItem;
        const item = inv?.attributes ? { id: inv.id, documentId: inv.documentId || inv.id, ...inv.attributes } : inv;
        if (!item?.id) return null;
        return {
          bulkLineId: row?.id || lineRow?.id || null,
          inventoryItemId: item.id,
          inventoryItemDocumentId: item.documentId || "",
          sku: item.sku || "",
          name: item.name || "",
          quantity: row?.quantity ?? 0,
          usageType: String(row?.usageType || (row?.isOptional ? "manual_optional" : "always")).toLowerCase(),
          usageRate: row?.usageRate ?? 1,
          notes: row?.notes || "",
        };
      })
      .filter(Boolean);
  }, [buildSheet?.bulkLines]);

  const conditionalBulkLines = useMemo(
    () => normalizedBulkLines.filter((lineRow) => lineRow.usageType === "conditional"),
    [normalizedBulkLines]
  );

  const numberOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const evaluateMaterialRuleLocal = (rule, values) => {
    const compareField = String(rule?.compareField || rule?.field || "actualValue");
    const actualValue = numberOrNull(values?.actualValue);
    const afterFixValue = numberOrNull(values?.afterFixValue);
    const resultStatus = String(values?.resultStatus || "").trim().toLowerCase();
    const replacementSource = String(values?.replacementSource || "new").trim().toLowerCase();
    const minValue = numberOrNull(values?.minValue);
    const passValue = numberOrNull(values?.passValue);
    const compareValue =
      compareField === "afterFixValue"
        ? afterFixValue
        : compareField === "finalValue"
        ? afterFixValue ?? actualValue
        : actualValue;

    const op = String(rule?.operator || "").trim().toLowerCase();
    const condition = String(rule?.condition || rule?.type || "").replace(/\s+/g, "").toLowerCase();
    const threshold = numberOrNull(rule?.threshold ?? rule?.value);
    const lower = numberOrNull(rule?.min ?? rule?.minValue);
    const upper = numberOrNull(rule?.max ?? rule?.maxValue);
    const reference = threshold ?? minValue ?? passValue;

    if (condition === "failed" || condition === "fail" || condition === "damaged" || condition === "resultstatus=fail") {
      if (replacementSource === "bank" || replacementSource === "parts_bank") return false;
      return ["fail", "failed", "damaged", "replaced"].includes(resultStatus);
    }
    if (condition === "passed" || condition === "pass" || condition === "resultstatus=pass") return resultStatus === "pass" || resultStatus === "passed";
    if (compareValue === null) return false;
    if (condition === "below_min" || condition === "belowmin" || condition === "actualvalue<minvalue" || condition === "<min") return minValue !== null && compareValue < minValue;
    if (condition === "at_or_above_min" || condition === "atorabovemin" || condition === "actualvalue>=minvalue" || condition === ">=min") return minValue !== null && compareValue >= minValue;
    if (condition === "between" || op === "between") return lower !== null && upper !== null && compareValue >= lower && compareValue <= upper;
    if (reference === null) return false;
    if (op === "<") return compareValue < reference;
    if (op === "<=") return compareValue <= reference;
    if (op === ">") return compareValue > reference;
    if (op === ">=") return compareValue >= reference;
    if (op === "=" || op === "==" || op === "eq") return compareValue === reference;
    return false;
  };

  const findConditionalBulkItemForRule = (rule) => {
    const bulkLineRef = rule?.bulkLineId ?? rule?.bulkLineKey ?? null;
    if (bulkLineRef !== null && bulkLineRef !== undefined && bulkLineRef !== "") {
      const byBulkLine = conditionalBulkLines.find((lineRow) => String(lineRow.bulkLineId || "") === String(bulkLineRef));
      if (byBulkLine) return byBulkLine;
    }
    const ref = rule?.inventoryItem ?? rule?.inventoryItemId ?? rule?.item ?? rule?.itemId ?? null;
    if (ref === null || ref === undefined || ref === "") return null;
    const raw = typeof ref === "object" ? ref.documentId || ref.id : ref;
    return conditionalBulkLines.find(
      (lineRow) => String(lineRow.inventoryItemDocumentId || "") === String(raw) || String(lineRow.inventoryItemId || "") === String(raw)
    ) || null;
  };

  const getTestEntryMaterialDecision = (test, testIdx, entry, entryIdx) => {
    const rules = Array.isArray(entry?.materialRules) ? entry.materialRules : [];
    if (!rules.length) return { hasRules: false, matched: [], pending: false };
    const entryKey = `${testIdx}-${entryIdx}`;
    const entryData = testEntryValues[entryKey] || {};
    const passValueNum = numberOrNull(entry?.passValue);
    const toleranceNum = numberOrNull(entry?.tolerance);
    const minValueNum = numberOrNull(entry?.minValue) ?? (passValueNum !== null && toleranceNum !== null ? passValueNum - toleranceNum : null);
    const values = {
      passValue: passValueNum,
      minValue: minValueNum,
      actualValue: entryData.actualValue !== undefined ? entryData.actualValue : entry?.actualValue,
      afterFixValue: entryData.afterFixValue !== undefined ? entryData.afterFixValue : entry?.afterFixValue,
      resultStatus: entryData.resultStatus !== undefined ? entryData.resultStatus : entry?.resultStatus,
      replacementSource: entryData.replacementSource || entry?.replacementSource || "new",
    };
    const isPassFail = String(entry?.resultType || "").toLowerCase() === "pass_fail";
    const hasValue = isPassFail
      ? !!String(values.resultStatus || "").trim()
      : numberOrNull(values.actualValue) !== null || numberOrNull(values.afterFixValue) !== null;
    const matched = rules
      .filter((rule) => evaluateMaterialRuleLocal(rule, values))
      .map((rule) => {
        const item = findConditionalBulkItemForRule(rule);
        return item
          ? {
              ...item,
              quantity: Number(rule?.quantity ?? item.quantity ?? 1),
              reason: rule?.reason || rule?.label || "",
              testName: test?.testName || test?.name || `Test ${testIdx + 1}`,
              testEntryName: entry?.valueName || `Entry ${entryIdx + 1}`,
              ruleId: rule?.id || rule?.ruleId || null,
              buildSheetBulkLineId: item.bulkLineId || rule?.bulkLineId || null,
            }
          : null;
      })
      .filter(Boolean);
    return { hasRules: true, matched, pending: !hasValue };
  };

  const normalizedIdentifiers = useMemo(() => {
    const ids = asArray(buildSheet?.identifiers);
    return ids
      .map((ident) => {
        const d = ident?.attributes || ident;
        const entries = asArray(d?.entries)
          .map((e) => {
            const ed = e?.attributes || e;
            return { name: ed?.name || "" };
          })
          .filter((e) => e.name && e.name.trim() !== "");
        return { name: d?.name || "", description: d?.description || "", required: d?.required === true || d?.required === "true", entries };
      })
      .filter((i) => i.name && i.name.trim() !== "");
  }, [buildSheet?.identifiers]);

  const identifierValuesMap = useMemo(() => {
    const raw = asArray(stageRecordData?.identifierValues);
    const map = {};
    raw.forEach((val) => {
      const v = val?.attributes || val;
      const identifierName = v?.identifierName || "";
      const fieldName = v?.fieldName || "";
      if (!identifierName || !fieldName) return;
      map[`${identifierName}::${fieldName}`] = {
        data: v?.data || "",
        change: v?.change || "",
        images: asArray(v?.images?.data || v?.images),
      };
    });
    return map;
  }, [stageRecordData?.identifierValues]);

  const testResultsByType = useMemo(() => {
    const raw = stageRecordData?.testResults;
    const arr = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
    const map = {};
    arr.forEach((tr) => {
      const d = tr?.attributes || tr;
      const type = d?.testType || d?.type || "";
      if (!type) return;
      map[type] = d;
    });
    return map;
  }, [stageRecordData?.testResults]);

  useEffect(() => {
    if (!normalizedTests.length) return;
    const next = {};
    normalizedTests.forEach((test, testIdx) => {
      const tr = testResultsByType?.[test.testName] || null;
      const savedEntries = Array.isArray(tr?.parameters?.testEntries) ? tr.parameters.testEntries : [];
      (test.testEntries || []).forEach((entry, entryIdx) => {
        const valueName = entry.valueName || entry.name || `Value ${entryIdx + 1}`;
        const saved = savedEntries.find((row) => (row?.valueName || row?.name) === valueName) || savedEntries[entryIdx] || {};
        next[`${testIdx}-${entryIdx}`] = {
          actualValue: saved.actualValue ?? entry.actualValue ?? "",
          afterFixValue: saved.afterFixValue ?? entry.afterFixValue ?? "",
          resultStatus: saved.resultStatus || entry.resultStatus || "",
          replacementNote: saved.replacementNote || "",
          replacementSource: saved.replacementSource || "",
          partsBankLineKey: saved.partsBankLineKey || "",
          partsBankPartName: saved.partsBankPartName || "",
        };
      });
    });
    setTestEntryValues(next);
  }, [normalizedTests, testResultsByType]);

  useEffect(() => {
    const raw = asArray(stageRecordData?.qualityCheckValues);
    if (!raw.length) return;
    const next = {};
    raw.forEach((entry) => {
      const row = entry?.attributes || entry;
      const idx = row?.checkIndex ?? 0;
      const name = row?.checkName || `Quality Check ${Number(idx) + 1}`;
      next[`${idx}::${name}`] = {
        notes: row?.notes || "",
        images: asArray(row?.images?.data || row?.images),
      };
    });
    setQualityCheckValues(next);
  }, [stageRecordData?.qualityCheckValues]);

  const uploadIds = useMemo(() => collectNumericMediaIds({ buildSheet, stageRecordData }), [buildSheet, stageRecordData]);
  const uploadKey = useMemo(() => uploadIds.slice().sort((a, b) => a - b).join(","), [uploadIds]);

  const { data: uploadMap } = useQuery({
    queryKey: ["m-upload-files", uploadKey],
    queryFn: async () => {
      const ids = uploadIds.slice().filter((n) => isValidStrapiUploadFileId(n));
      if (!ids.length) return {};
      const entries = await Promise.all(
        ids.map(async (fileId) => {
          try {
            const res = await api.get(`/api/upload/files/${fileId}`);
            return [String(fileId), res?.data || null];
          } catch {
            return [String(fileId), null];
          }
        })
      );
      const map = {};
      entries.forEach(([k, v]) => {
        if (v) map[k] = v;
      });
      return map;
    },
    enabled: uploadIds.length > 0,
    staleTime: 60_000,
  });

  const { data: componentDemandPreviewRaw, isFetching: componentDemandPreviewLoading, error: componentDemandPreviewError } = useQuery({
    queryKey: ["m-component-demand-preview", unitRefForComponentOrder],
    queryFn: () => get(`/api/inventory-orders/preview-valve-body/${unitRefForComponentOrder}`),
    enabled: !!unitRefForComponentOrder && tab === 7,
    staleTime: 1000,
  });
  const componentDemandPreview = componentDemandPreviewRaw?.data || componentDemandPreviewRaw?.data?.data || componentDemandPreviewRaw || null;
  const componentDemandLines = Array.isArray(componentDemandPreview?.lines) ? componentDemandPreview.lines : [];
  const componentFulfilledLines = Array.isArray(componentDemandPreview?.fulfilledLines) ? componentDemandPreview.fulfilledLines : [];
  const liveConditionalMaterialDemand = useMemo(() => {
    const aggregated = new Map();
    const pending = [];
    normalizedTests.forEach((test, testIdx) => {
      (test.testEntries || []).forEach((entry, entryIdx) => {
        const decision = getTestEntryMaterialDecision(test, testIdx, entry, entryIdx);
        if (!decision.hasRules) return;
        if (decision.pending) {
          pending.push({
            testName: test?.testName || test?.name || `Test ${testIdx + 1}`,
            testEntryName: entry?.valueName || `Entry ${entryIdx + 1}`,
          });
          return;
        }
        decision.matched.forEach((item) => {
          const key = String(item.inventoryItemId);
          const prev = aggregated.get(key) || {
            inventoryItemId: item.inventoryItemId,
            sku: item.sku,
            name: item.name,
            qtyRequested: 0,
            sources: [],
          };
          prev.qtyRequested += Number(item.quantity || 0);
          prev.sources.push({
            source: "test_rule",
            testName: item.testName,
            testEntryName: item.testEntryName,
            reason: item.reason,
            ruleId: item.ruleId,
            buildSheetBulkLineId: item.buildSheetBulkLineId || null,
          });
          aggregated.set(key, prev);
        });
      });
    });
    return { lines: Array.from(aggregated.values()), pending };
  }, [normalizedTests, testEntryValues, conditionalBulkLines]);

  const displayedComponentDemandLines = useMemo(() => {
    const hasLiveDecisions = liveConditionalMaterialDemand.lines.length > 0 || liveConditionalMaterialDemand.pending.length > 0;
    if (!hasLiveDecisions) return componentDemandLines;
    const alwaysLines = componentDemandLines.filter((row) => {
      const firstSource = Array.isArray(row?.sources) ? row.sources[0] : null;
      return firstSource?.source !== "test_rule";
    });
    return [...alwaysLines, ...liveConditionalMaterialDemand.lines];
  }, [componentDemandLines, liveConditionalMaterialDemand]);

  const componentMissingFulfilledLines = useMemo(() => {
    const needed = new Map();
    displayedComponentDemandLines.forEach((row) => {
      const key = Number(row?.inventoryItemId);
      if (!Number.isFinite(key) || key <= 0) return;
      needed.set(key, (needed.get(key) || 0) + (Number(row?.qtyRequested || 0) || 0));
    });
    const fulfilled = new Map();
    componentFulfilledLines.forEach((row) => {
      const key = Number(row?.inventoryItemId);
      if (!Number.isFinite(key) || key <= 0) return;
      fulfilled.set(key, (fulfilled.get(key) || 0) + (Number(row?.qtyFulfilled ?? row?.qtyUsed ?? 0) || 0));
    });
    return Array.from(needed.entries())
      .map(([inventoryItemId, qtyNeeded]) => {
        const qtyFulfilled = fulfilled.get(inventoryItemId) || 0;
        if (qtyFulfilled + 0.000001 >= qtyNeeded) return null;
        const lineForItem = displayedComponentDemandLines.find((row) => Number(row?.inventoryItemId) === inventoryItemId) || {};
        return { inventoryItemId, sku: lineForItem.sku || "", name: lineForItem.name || "", qtyNeeded, qtyFulfilled };
      })
      .filter(Boolean);
  }, [displayedComponentDemandLines, componentFulfilledLines]);

  const { data: partsBankTemplatesRaw } = useQuery({
    queryKey: ["m-parts-bank-templates", coreItemTemplateRefKey],
    queryFn: async () => {
      const filters = { enabled: { $eq: true } };
      const or = [];
      if (coreItemNumericIdForTemplate) or.push({ coreItem: { id: { $eq: coreItemNumericIdForTemplate } } });
      if (coreItemDocumentIdForTemplate) or.push({ coreItem: { documentId: { $eq: coreItemDocumentIdForTemplate } } });
      if (!or.length) return { data: [] };
      return get("/api/parts-bank-templates", {
        populate: { coreItem: true, bankItem: true, lines: { populate: { referenceImages: true } } },
        sort: "name:asc",
        filters: { ...filters, $or: or },
        pagination: { pageSize: 200 },
      });
    },
    enabled: !!coreItemTemplateRefKey && (tab === 8 || tab === 2),
    staleTime: 5000,
  });
  const partsBankTemplates = useMemo(() => {
    const rows = (partsBankTemplatesRaw?.data || []).map(normalize);
    return rows.filter((template) => {
      const core = template?.coreItem?.data || template?.coreItem || null;
      const bank = template?.bankItem?.data || template?.bankItem || null;
      return !!((core?.id || core?.documentId) && (bank?.id || bank?.documentId));
    });
  }, [partsBankTemplatesRaw]);
  useEffect(() => {
    if (!partsBankTemplates.length) {
      if (partsBankTemplateId) setPartsBankTemplateId("");
      return;
    }
    const ref = String(partsBankTemplateId || "").trim();
    const exists = ref && partsBankTemplates.some((template) => String(template.id) === ref || String(template.documentId) === ref);
    if (!exists) setPartsBankTemplateId(String(partsBankTemplates[0].id || partsBankTemplates[0].documentId));
  }, [partsBankTemplates, partsBankTemplateId]);
  const selectedTemplate = useMemo(() => {
    const ref = String(partsBankTemplateId || "").trim();
    return partsBankTemplates.find((template) => String(template.id) === ref || String(template.documentId) === ref) || null;
  }, [partsBankTemplates, partsBankTemplateId]);
  const selectedTemplateRef = selectedTemplate ? String(selectedTemplate.id || selectedTemplate.documentId || "") : "";
  const { data: partsBankAvailRaw } = useQuery({
    queryKey: ["m-parts-bank-template-availability", selectedTemplateRef],
    queryFn: () => get(`/api/parts-bank-templates/${selectedTemplateRef}/availability`),
    enabled: !!selectedTemplateRef && (tab === 8 || tab === 2),
    staleTime: 1500,
  });
  const partsBankAvailByKey = useMemo(() => {
    const by = partsBankAvailRaw?.byLineKey || partsBankAvailRaw?.data?.byLineKey || partsBankAvailRaw?.data?.data?.byLineKey || {};
    return by && typeof by === "object" ? by : {};
  }, [partsBankAvailRaw]);
  const availablePartsBankLines = useMemo(() => {
    const rows = selectedTemplate?.lines?.data || selectedTemplate?.lines || selectedTemplate?.attributes?.lines || [];
    return (Array.isArray(rows) ? rows : [])
      .map((row) => row?.attributes || row)
      .filter((row) => {
        const key = String(row?.lineKey || "").trim();
        if (!key) return false;
        const remaining = Number(partsBankAvailByKey?.[key]?.qtyRemaining ?? 0) || 0;
        return remaining > 0;
      });
  }, [selectedTemplate, partsBankAvailByKey]);
  const partsBankTemplateLines = useMemo(() => {
    const search = String(partsBankSearch || "").trim().toLowerCase();
    if (!search) return availablePartsBankLines;
    return availablePartsBankLines.filter((row) => `${row?.partName || ""} ${row?.group || ""}`.toLowerCase().includes(search));
  }, [availablePartsBankLines, partsBankSearch]);

  const { data: unitUsagesRaw } = useQuery({
    queryKey: ["m-parts-bank-usages-for-unit", unitRefForPartsBank],
    queryFn: () => {
      const numeric = Number(unitRefForPartsBank);
      const or = [];
      if (Number.isFinite(numeric)) or.push({ valveBody: { id: { $eq: numeric } } });
      or.push({ valveBody: { documentId: { $eq: unitRefForPartsBank } } });
      return get("/api/parts-bank-usages", {
        sort: "usedAt:desc",
        filters: { $or: or },
        populate: { lot: true, workOrder: true },
        pagination: { pageSize: 2000 },
      });
    },
    enabled: !!unitRefForPartsBank && tab === 8,
    staleTime: 1000,
  });
  const unitUsages = useMemo(() => (unitUsagesRaw?.data || []).map(normalize), [unitUsagesRaw]);

  const componentOrderMutation = useMutation({
    mutationFn: async () => {
      const unitRef = unit?.documentId || unit?.id;
      if (!unitRef) throw new Error("Unit reference is missing.");
      return post(`/api/inventory-orders/from-valve-body/${unitRef}`);
    },
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["m-valve-body", documentId] });
      await qc.invalidateQueries({ queryKey: ["m-inventory-orders"] });
      setActionNotice({ type: "success", text: "Component inventory order created." });
    },
    onError: (error) => {
      setActionNotice({
        type: "error",
        text: error?.response?.data?.error?.message || error?.response?.data?.error || error?.message || "Failed to create component inventory order.",
      });
    },
  });

  const startBuildMutation = useMutation({
    mutationFn: () =>
      post("/api/stage-records", {
        data: {
          status: "in_progress",
          workOrder: wo?.documentId || wo?.id || null,
          unit: unit?.documentId || unit?.id,
          technicianNotes: [],
          startedAt: new Date().toISOString(),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-unit-latest-stage-record", unitKey] });
      qc.invalidateQueries({ queryKey: ["m-valve-body", documentId] });
      setActionNotice({ type: "success", text: "Build started." });
    },
    onError: (error) => setActionNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to start build." }),
  });

  const buildTestEntriesData = (test, testIdx) =>
    (test.testEntries || []).map((entry, entryIdx) => {
      const values = testEntryValues[`${testIdx}-${entryIdx}`] || {};
      const passValue = entry.passValue !== undefined && entry.passValue !== null && entry.passValue !== "" ? Number(entry.passValue) : null;
      const tolerance = entry.tolerance !== undefined && entry.tolerance !== null && entry.tolerance !== "" ? Number(entry.tolerance) : null;
      const minValue =
        entry.minValue !== undefined && entry.minValue !== null && entry.minValue !== ""
          ? Number(entry.minValue)
          : passValue !== null && tolerance !== null
          ? passValue - tolerance
          : null;
      return {
        valueName: entry.valueName || entry.name || "",
        passValue: Number.isFinite(passValue) ? passValue : null,
        tolerance: Number.isFinite(tolerance) ? tolerance : null,
        minValue: Number.isFinite(minValue) ? minValue : null,
        resultType: entry.resultType || "numeric",
        resultStatus: values.resultStatus || null,
        replacementNote: values.replacementNote || null,
        replacementSource: values.replacementSource || null,
        partsBankLineKey: values.partsBankLineKey || null,
        partsBankPartName: values.partsBankPartName || null,
        actualValue: values.actualValue !== "" && values.actualValue != null ? Number(values.actualValue) : null,
        afterFixValue: values.afterFixValue !== "" && values.afterFixValue != null ? Number(values.afterFixValue) : null,
      };
    });

  const saveTestMutation = useMutation({
    mutationFn: async ({ test, testIdx }) => {
      if (!stageRecordData) throw new Error("Start build first.");
      const testType = test?.testName || test?.name || `Test ${testIdx + 1}`;
      const stageRecordId = stageRecordData.documentId || stageRecordData.id;
      const entries = buildTestEntriesData(test, testIdx);
      const existing = await get("/api/test-results", {
        publicationState: "preview",
        filters: {
          $and: [
            { testType: { $eq: testType } },
            { $or: [{ stageRecord: { id: { $eq: stageRecordData.id } } }, { stageRecord: { documentId: { $eq: String(stageRecordId) } } }] },
          ],
        },
        sort: "createdAt:desc",
        pagination: { pageSize: 1 },
      });
      const existingRow = Array.isArray(existing?.data) ? existing.data[0] : null;
      const existingId = existingRow?.documentId || existingRow?.id || null;
      const hasFailed = entries.some((entry) => String(entry.resultType).toLowerCase() === "pass_fail" && ["fail", "failed", "damaged"].includes(String(entry.resultStatus || "").toLowerCase()));
      const dataPayload = { parameters: { testEntries: entries }, result: hasFailed ? "fail" : "pass" };
      if (existingId) return put(`/api/test-results/${existingId}`, { data: dataPayload });
      return post("/api/test-results", { data: { testType, ...dataPayload, stageRecord: stageRecordId } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-unit-latest-stage-record", unitKey] });
      qc.invalidateQueries({ queryKey: ["m-component-demand-preview", unitRefForComponentOrder] });
      setActionNotice({ type: "success", text: "Test result saved." });
    },
    onError: (error) => setActionNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to save test result." }),
  });

  const uploadQualityCheckImages = async (checkIdx, checkName, files) => {
    if (!files || !files.length) return;
    if (!stageRecordData) {
      setActionNotice({ type: "error", text: "Start build before uploading QC images." });
      return;
    }
    try {
      const uploads = await Promise.all(
        Array.from(files).map((file) => {
          const formData = new FormData();
          formData.append("files", file);
          return api.post("/api/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        })
      );
      const uploadedFiles = uploads.flatMap((res) => res.data || []);
      const key = `${checkIdx}::${checkName}`;
      setQualityCheckValues((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || { notes: "", images: [] }),
          images: [...((prev[key] || {}).images || []), ...uploadedFiles],
        },
      }));
      setActionNotice({ type: "success", text: "Images uploaded. Save QC to keep them on this build." });
    } catch (error) {
      setActionNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to upload images." });
    }
  };

  const saveQualityCheckValuesMutation = useMutation({
    mutationFn: async () => {
      if (!stageRecordData) throw new Error("Start build first.");
      const valuesArray = [];
      normalizedQualityChecks.forEach((check, idx) => {
        const checkName = check?.name || check?.title || `Quality Check ${idx + 1}`;
        const value = qualityCheckValues[`${idx}::${checkName}`];
        const imageIds = (value?.images || [])
          .map((img) => (typeof img === "number" ? img : img?.id || img?.data?.id || null))
          .filter(Boolean);
        if (!value?.notes && !imageIds.length) return;
        valuesArray.push({ checkIndex: idx, checkName, notes: value.notes || "", images: imageIds });
      });
      return put(`/api/stage-records/${stageRecordData.documentId || stageRecordData.id}`, {
        data: { qualityCheckValues: valuesArray },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-unit-latest-stage-record", unitKey] });
      setActionNotice({ type: "success", text: "Quality checks saved." });
    },
    onError: (error) => setActionNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to save quality checks." }),
  });

  const partsBankRequestMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplateRef) throw new Error("No parts bank template selected.");
      const lines = partsBankTemplateLines
        .map((row) => {
          const key = String(row?.lineKey || "").trim();
          const qty = Number(partsBankQtyByLineKey?.[key] ?? 0) || 0;
          if (!key || qty <= 0) return null;
          return {
            lineKey: key,
            partName: row?.partName || "(unnamed)",
            group: row?.group || "",
            referenceImages: asArray(row?.referenceImages?.data || row?.referenceImages),
            qtyRequested: qty,
          };
        })
        .filter(Boolean);
      if (!lines.length) throw new Error("Enter at least one parts bank quantity.");
      return post("/api/inventory-orders/parts-bank", {
        data: {
          workOrderId: wo?.documentId || wo?.id || null,
          valveBodyId: unitRefForPartsBank,
          stageRecordId: stageRecordData?.id || null,
          requestedBy: null,
          notes: `Parts bank request for valve body ${String(displayTag || unitRefForPartsBank)}`,
          lines: lines.map((lineRow) => ({
            type: "parts_bank",
            partsBankTemplateId: selectedTemplateRef,
            lineKey: lineRow.lineKey,
            partName: lineRow.partName,
            group: lineRow.group || null,
            referenceImages: lineRow.referenceImages || [],
            qtyRequested: lineRow.qtyRequested,
            qtyFulfilled: 0,
          })),
        },
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["m-parts-bank-template-availability", selectedTemplateRef] });
      await qc.invalidateQueries({ queryKey: ["m-inventory-orders"] });
      setPartsBankQtyByLineKey({});
      setActionNotice({ type: "success", text: "Parts bank inventory order created." });
    },
    onError: (error) => {
      setActionNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to create parts bank order." });
    },
  });

  const resolveFileFromAny = (file) => {
    if (!file) return null;
    if (isValidStrapiUploadFileId(file))
      return uploadMap?.[String(file)] || { id: file };
    const idFrom = getMediaId(file);
    if (idFrom != null && uploadMap?.[String(idFrom)] && !getFileUrl(file, baseURL)) return uploadMap[String(idFrom)];
    return file;
  };

  useEffect(() => {
    if (!unitKey) return;
    try {
      const stored = window.localStorage.getItem(`vb-step-checklist-${unitKey}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object") setStepChecklistState(parsed);
      }
    } catch {
      // ignore
    }
  }, [unitKey]);

  const handleToggleStepChecklist = (stepIndex, checkIndex) => {
    setStepChecklistState((prev) => {
      const key = `${stepIndex}-${checkIndex}`;
      const next = { ...(prev || {}), [key]: !prev?.[key] };
      try {
        if (unitKey) window.localStorage.setItem(`vb-step-checklist-${unitKey}`, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const openViewer = ({ file, title, annotations }) => {
    setViewer({
      open: true,
      file,
      title: title || "",
      annotations: Array.isArray(annotations) ? annotations : [],
    });
  };

  return (
    <IosPage>
      <MobileTopBar
        title={displayTag}
        subtitle={fg?.sku ? `SKU ${fg.sku}` : "Valve body"}
        onBack={() => {
          if ((window.history.state?.idx || 0) > 0) navigate(-1);
          else navigate("/m/valve-bodies");
        }}
        right={
          wo?.documentId || wo?.id ? (
            <IconButton aria-label="Open work order" size="small" onClick={() => navigate(`/m/work-orders/${wo.documentId || wo.id}`)}>
              <Icon fontSize="small">build</Icon>
            </IconButton>
          ) : null
        }
      />

      {isLoading ? <LinearProgress sx={{ mt: 1 }} /> : null}

      {!unit && !isLoading ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Valve body not found.
        </Typography>
      ) : null}

      {unit ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <IosCard sx={{ p: 1.25 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                  <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>
                    Valve body
                  </Typography>
                  <Typography sx={{ color: iosColors.ink, fontSize: 20, fontWeight: 950, lineHeight: 1.08 }}>
                    {displayTag}
                  </Typography>
                  <Typography sx={{ color: iosColors.muted, fontSize: 13, mt: 0.25 }}>
                    {fg?.sku ? `SKU ${fg.sku}` : "No SKU"} • {wo?.workOrderNumber ? `WO ${wo.workOrderNumber}` : "No work order"}
                  </Typography>
                </Box>
                <Chip size="small" label={stageChip.label} color={stageChip.color} variant="outlined" sx={{ fontWeight: 900, flexShrink: 0 }} />
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${normalizedSteps.length || 0} steps`} variant="outlined" sx={{ fontWeight: 850 }} />
                <Chip size="small" label={`${normalizedTests.length || 0} tests`} variant="outlined" sx={{ fontWeight: 850 }} />
                <Chip size="small" label={`${normalizedQualityChecks.length || 0} QC`} variant="outlined" sx={{ fontWeight: 850 }} />
                {buildSheet?.name ? <Chip size="small" label={buildSheet.name} variant="outlined" sx={{ fontWeight: 850 }} /> : null}
              </Stack>
            </Stack>
          </IosCard>

          <IosFilterBar label="Valve body workflow" value={tab} options={sectionOptions} onChange={setTab} />
              {tab === 0 ? (
                <Stack spacing={0.75}>
                  <IosCard>
                    <Stack spacing={0.85}>
                      <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
                        Shortcuts
                      </Typography>
                      {actionNotice ? (
                        <Typography sx={{ color: actionNotice.type === "success" ? iosColors.green : iosColors.red, fontSize: 12.5, fontWeight: 850 }}>
                          {actionNotice.text}
                        </Typography>
                      ) : null}
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0.75 }}>
                        <ActionShortcut icon="precision_manufacturing" label="Materials" tone="orange" onClick={() => setTab(7)} />
                        <ActionShortcut icon="shopping_cart" label="Parts Bank" tone="green" onClick={() => setTab(8)} />
                        <ActionShortcut
                          icon="play_circle"
                          label="Start"
                          tone="green"
                          disabled={!!stageRecordData || startBuildMutation.isPending}
                          onClick={() => startBuildMutation.mutate()}
                        />
                        <ActionShortcut
                          icon="science"
                          label="Tests"
                          tone="green"
                          onClick={() => setTab(2)}
                        />
                      </Box>
                    </Stack>
                  </IosCard>
                  <Card variant="outlined">
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                        Details
                      </Typography>
                      <Stack spacing={0.6}>
                        <Row label="Tag" value={displayTag} />
                        <Row label="Serial" value={unit.serialNumber || "—"} />
                        <Row label="OEM" value={unit.oem || "—"} />
                        <Row label="Transmission" value={unit.transmissionModel || "—"} />
                        <Row label="Core condition" value={unit.coreCondition || "—"} />
                        <Row label="Received" value={fmtDate(unit.receivedAt)} />
                        <Row label="Current stage" value={stageRecordData?.stage?.data?.attributes?.name || stageRecordData?.stage?.name || "—"} />
                        <Row label="Stage status" value={stageRecordData?.status || "—"} />
                        <Row label="Stage started" value={fmtDateTime(stageRecordData?.startedAt)} />
                        <Row label="Stage completed" value={fmtDateTime(stageRecordData?.completedAt)} />
                      </Stack>
                    </Box>
                  </Card>

                  {(() => {
                    const notesRaw = stageRecordData?.technicianNotes;
                    const notesArr = Array.isArray(notesRaw)
                      ? notesRaw
                      : typeof notesRaw === "string" && notesRaw.trim()
                        ? [{ note: notesRaw }]
                        : [];
                    const notes = notesArr
                      .map((n) => {
                        const d = n?.attributes || n;
                        return { note: d?.note || d?.text || "", createdAt: d?.createdAt || null };
                      })
                      .filter((n) => n.note && n.note.trim() !== "");
                    if (!notes.length) return null;
                    return (
                      <Card variant="outlined">
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                            Technician notes
                          </Typography>
                          <Stack spacing={0.75}>
                            {notes.slice(0, 30).map((n, idx) => (
                              <Card key={idx} variant="outlined">
                                <Box sx={{ p: 1.0 }}>
                                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                    {n.note}
                                  </Typography>
                                  {n.createdAt ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                      {fmtDateTime(n.createdAt)}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Card>
                            ))}
                          </Stack>
                        </Box>
                      </Card>
                    );
                  })()}

                  {(() => {
                    const atts = asArray(stageRecordData?.attachments?.data || stageRecordData?.attachments);
                    if (!atts.length) return null;
                    return (
                      <Card variant="outlined">
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                            Attachments
                          </Typography>
                          <Stack spacing={0.75}>
                            {atts.slice(0, 30).map((a, idx) => {
                              const f = resolveFileFromAny(a);
                              const url = getFileUrl(f, baseURL);
                              if (!url) return null;
                              return (
                                <Card
                                  key={String(getMediaId(a) || idx)}
                                  variant="outlined"
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => openViewer({ file: f, title: getFileName(f) || `Attachment ${idx + 1}`, annotations: [] })}
                                >
                                  <Box sx={{ p: 1.0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                    <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                                      <Typography variant="body2" fontWeight={900}>
                                        {getFileName(f) || `Attachment ${idx + 1}`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {getFileType(f) || "file"}
                                      </Typography>
                                    </Box>
                                    <Icon fontSize="small">open_in_new</Icon>
                                  </Box>
                                </Card>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Card>
                    );
                  })()}

                  {photos.length ? (
                    <Card variant="outlined">
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                          Photos
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
                          {photos.slice(0, 24).map((p) => {
                            const file = p?.attributes ? { id: p.id, ...p.attributes } : p;
                            const src = getFileUrl(file, baseURL);
                            return (
                              <Box
                                key={String(file?.id || src || Math.random())}
                                component="img"
                                src={src || ""}
                                alt={String(file?.name || "Photo")}
                                sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 12, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
                                onClick={() => openViewer({ file: p, title: file?.name || "Photo", annotations: [] })}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Card>
                  ) : null}

                  {coreReturns.length ? (
                    <Card variant="outlined">
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                          Core returns
                        </Typography>
                        <Stack spacing={0.75}>
                          {coreReturns.slice(0, 10).map((cr) => {
                            const c = normalize(cr);
                            const inv = c?.invoice?.data ? normalize(c.invoice.data) : c?.invoice ? normalize(c.invoice) : null;
                            return (
                              <Card key={String(c.documentId || c.id)} variant="outlined">
                                <Box sx={{ p: 1.0 }}>
                                  <Stack spacing={0.25}>
                                    <Typography variant="body2" fontWeight={900}>
                                      {c.status || "—"} • {fmtDate(c.returnedAt)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Amount: {c.amount != null ? String(c.amount) : "—"}
                                      {c.coreSerialNumber ? ` • Core SN: ${c.coreSerialNumber}` : ""}
                                      {inv?.salesOrderNumber ? ` • Invoice: ${inv.salesOrderNumber}` : ""}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Card>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Card>
                  ) : null}

                  <Card variant="outlined">
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                        Links
                      </Typography>
                      <Stack spacing={0.75}>
                        <LinkRow label="Open in Pipeline" disabled={!unitKey} onClick={() => navigate(`/m/pipeline`)} />
                        <LinkRow label="Work order" disabled={!(wo?.documentId || wo?.id)} onClick={() => navigate(`/m/work-orders/${wo.documentId || wo.id}`)} />
                        <LinkRow label="Finished good" disabled={!(fg?.documentId || fg?.id)} onClick={() => navigate(`/m/inventory/${fg.documentId || fg.id}`)} />
                      </Stack>
                    </Box>
                  </Card>
                </Stack>
              ) : null}

              {tab === 1 ? (
                <Stack spacing={0.75}>
                  {normalizedSteps.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No steps defined on build sheet.
                    </Typography>
                  ) : (
                    normalizedSteps.map((step, idx) => {
                      const hasChecklist = Array.isArray(step.checklist) && step.checklist.length > 0;
                      const subSteps = normalizeSubSteps(step.subSteps || step.sub_steps);
                      return (
                        <Accordion
                          key={String(step.id || idx)}
                          expanded={expandedStep === idx}
                          onChange={() => setExpandedStep(expandedStep === idx ? false : idx)}
                          disableGutters
                          elevation={0}
                          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", "&:before": { display: "none" } }}
                        >
                          <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                            <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                              <Typography variant="body2" fontWeight={900}>
                                Step {step.sequence || idx + 1} • {step.title || "Untitled"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {hasChecklist ? `${step.checklist.length} checklist item(s)` : "No checklist"}
                                {subSteps.length ? ` • ${subSteps.length} sub-step(s)` : ""}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {step.body ? (
                              <Box sx={{ mb: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: "pre-wrap", "& p": { margin: 0 }, "& ul, & ol": { margin: "8px 0", paddingLeft: "24px" } }}
                                  dangerouslySetInnerHTML={{ __html: step.body }}
                                />
                              </Box>
                            ) : null}

                            {(step.tools?.length || step.torqueSpec) ? (
                              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
                                {step.tools && step.tools.length > 0 ? (
                                  <>
                                    <Chip size="small" label="Tools" variant="outlined" sx={{ fontWeight: 900 }} />
                                    {Array.isArray(step.tools)
                                      ? step.tools.map((t, tIdx) => <Chip key={tIdx} size="small" label={t} variant="outlined" sx={{ fontWeight: 900 }} />)
                                      : <Chip size="small" label={String(step.tools)} variant="outlined" sx={{ fontWeight: 900 }} />}
                                  </>
                                ) : null}
                                {step.torqueSpec ? <Chip size="small" label={`Torque: ${step.torqueSpec}`} color="info" variant="outlined" sx={{ fontWeight: 900 }} /> : null}
                              </Box>
                            ) : null}

                            {step.safetyNotes ? (
                              <Card variant="outlined" sx={{ borderColor: "warning.main", bgcolor: "warning.lighter", mb: 1 }}>
                                <Box sx={{ p: 1.0 }}>
                                  <Typography variant="caption" fontWeight={900} color="warning.main">
                                    Safety Notes
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {step.safetyNotes}
                                  </Typography>
                                </Box>
                              </Card>
                            ) : null}

                            {subSteps.length ? (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                                  Sub-steps
                                </Typography>
                                <Stack spacing={0.75}>
                                  {subSteps.map((s, sIdx) => (
                                    <Card key={String(s.id || sIdx)} variant="outlined" sx={{ bgcolor: "grey.50" }}>
                                      <Box sx={{ p: 1.0 }}>
                                        <Typography variant="body2" fontWeight={900}>
                                          {step.sequence || idx + 1}.{sIdx + 1} • {s.title || `Sub-step ${sIdx + 1}`}
                                        </Typography>
                                        {s.body ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>{s.body}</Typography> : null}

                                        {Array.isArray(s.images) && s.images.length ? (
                                          <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                              Images
                                            </Typography>
                                            <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.25 }}>
                                              {s.images.map((img, imgIdx) => {
                                                const f = resolveFileFromAny(img);
                                                const src = getFileUrl(f, baseURL);
                                                if (!src) return null;
                                                return (
                                                  <Box
                                                    key={String(getMediaId(img) || imgIdx)}
                                                    component="img"
                                                    src={src}
                                                    alt={getFileName(f) || `Image ${imgIdx + 1}`}
                                                    sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 12, border: "1px solid", borderColor: "divider" }}
                                                    onClick={() => {
                                                      const imgId = getMediaId(img);
                                                      const key = imgId != null ? String(imgId) : null;
                                                      const anns =
                                                        imgId != null
                                                          ? s?.imageAnnotations?.images?.[key]?.annotations ||
                                                            s?.imageAnnotations?.images?.[imgId]?.annotations ||
                                                            []
                                                          : [];
                                                      openViewer({
                                                        file: f,
                                                        title: `${step.title || "Step"} – ${s.title || `Sub-step ${sIdx + 1}`} – ${getFileName(f) || `Image ${imgIdx + 1}`}`,
                                                        annotations: Array.isArray(anns) ? anns : [],
                                                      });
                                                    }}
                                                  />
                                                );
                                              })}
                                            </Box>
                                          </Box>
                                        ) : null}

                                        {Array.isArray(s.documents) && s.documents.length ? (
                                          <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                              Documents
                                            </Typography>
                                            <Stack spacing={0.5}>
                                              {s.documents.map((doc, docIdx) => {
                                                const f = resolveFileFromAny(doc);
                                                const url = getFileUrl(f, baseURL);
                                                if (!url) return null;
                                                return (
                                                  <Card
                                                    key={String(getMediaId(doc) || docIdx)}
                                                    variant="outlined"
                                                    sx={{ cursor: "pointer" }}
                                                    onClick={() => openViewer({ file: f, title: getFileName(f) || `Document ${docIdx + 1}`, annotations: [] })}
                                                  >
                                                    <Box sx={{ p: 1.0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                      <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                                                        <Typography variant="body2" fontWeight={900}>{getFileName(f) || `Document ${docIdx + 1}`}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{getFileType(f) || "file"}</Typography>
                                                      </Box>
                                                      <Icon fontSize="small">open_in_new</Icon>
                                                    </Box>
                                                  </Card>
                                                );
                                              })}
                                            </Stack>
                                          </Box>
                                        ) : null}
                                      </Box>
                                    </Card>
                                  ))}
                                </Stack>
                              </Box>
                            ) : null}

                            {Array.isArray(step.images) && step.images.length ? (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                                  Images
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.25 }}>
                                  {step.images.map((img, imgIdx) => {
                                    const f = resolveFileFromAny(img);
                                    const src = getFileUrl(f, baseURL);
                                    if (!src) return null;
                                    return (
                                      <Box
                                        key={String(getMediaId(img) || imgIdx)}
                                        component="img"
                                        src={src}
                                        alt={getFileName(f) || `Image ${imgIdx + 1}`}
                                        sx={{ width: 112, height: 112, objectFit: "cover", borderRadius: 12, border: "1px solid", borderColor: "divider" }}
                                        onClick={() => {
                                          const imgId = getMediaId(img);
                                          const key = imgId != null ? String(imgId) : null;
                                          const anns =
                                            imgId != null
                                              ? step?.imageAnnotations?.images?.[key]?.annotations ||
                                                step?.imageAnnotations?.images?.[imgId]?.annotations ||
                                                []
                                              : [];
                                          openViewer({
                                            file: f,
                                            title: `${step.title || "Step"} – ${getFileName(f) || `Image ${imgIdx + 1}`}`,
                                            annotations: Array.isArray(anns) ? anns : [],
                                          });
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            ) : null}

                            {Array.isArray(step.documents) && step.documents.length ? (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                                  Documents
                                </Typography>
                                <Stack spacing={0.5}>
                                  {step.documents.map((doc, docIdx) => {
                                    const f = resolveFileFromAny(doc);
                                    const url = getFileUrl(f, baseURL);
                                    if (!url) return null;
                                    return (
                                      <Card
                                        key={String(getMediaId(doc) || docIdx)}
                                        variant="outlined"
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => openViewer({ file: f, title: getFileName(f) || `Document ${docIdx + 1}`, annotations: [] })}
                                      >
                                        <Box sx={{ p: 1.0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                          <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                                            <Typography variant="body2" fontWeight={900}>{getFileName(f) || `Document ${docIdx + 1}`}</Typography>
                                            <Typography variant="caption" color="text.secondary">{getFileType(f) || "file"}</Typography>
                                          </Box>
                                          <Icon fontSize="small">open_in_new</Icon>
                                        </Box>
                                      </Card>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            ) : null}

                            {hasChecklist ? (
                              <Card variant="outlined" sx={{ mt: 1 }}>
                                <Box sx={{ p: 1.0 }}>
                                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                                    Checklist
                                  </Typography>
                                  <Stack spacing={0.25}>
                                    {step.checklist.map((check, checkIdx) => {
                                      const stateKey = `${idx}-${checkIdx}`;
                                      const checked = !!stepChecklistState[stateKey];
                                      return (
                                        <Box key={checkIdx} sx={{ borderRadius: 1, bgcolor: check.required ? "error.lighter" : "transparent", px: 0.5 }}>
                                          <FormControlLabel
                                            sx={{ alignItems: "flex-start", m: 0, "& .MuiCheckbox-root": { mt: -0.3 } }}
                                            control={<Checkbox size="small" checked={checked} onChange={() => handleToggleStepChecklist(idx, checkIdx)} color={check.required ? "error" : "primary"} />}
                                            label={
                                              <Typography variant="body2" sx={{ fontWeight: 700, color: check.required ? "error.dark" : "text.primary" }}>
                                                {check.text}
                                                {check.required ? <Chip label="Required" size="small" color="error" sx={{ ml: 1, height: 18 }} /> : null}
                                              </Typography>
                                            }
                                          />
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                </Box>
                              </Card>
                            ) : null}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                </Stack>
              ) : null}

              {tab === 2 ? (
                <Stack spacing={0.75} sx={{ pb: !stageRecordData ? 9.5 : 0 }}>
                  <IosCard>
                    <Stack spacing={0.85}>
                      <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
                        Required tests
                      </Typography>
                      {actionNotice ? (
                        <Typography sx={{ color: actionNotice.type === "success" ? iosColors.green : iosColors.red, fontSize: 12.5, fontWeight: 850 }}>
                          {actionNotice.text}
                        </Typography>
                      ) : null}
                      {!stageRecordData ? (
                        <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>
                          Start the build before saving test results.
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Icon sx={{ color: iosColors.green, fontSize: 20 }}>check_circle</Icon>
                          <Typography sx={{ color: iosColors.green, fontSize: 13, fontWeight: 850 }}>Build is active. Test results can be saved.</Typography>
                        </Stack>
                      )}
                    </Stack>
                  </IosCard>
                  {normalizedTests.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No tests defined on build sheet.
                    </Typography>
                  ) : (
                    normalizedTests.map((test, testIdx) => {
                      const tr = testResultsByType?.[test.testName] || null;
                      const params = tr?.parameters || {};
                      const savedEntries = Array.isArray(params?.testEntries) ? params.testEntries : [];
                      return (
                        <Accordion
                          key={String(test.testName || testIdx)}
                          disableGutters
                          elevation={0}
                          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", "&:before": { display: "none" } }}
                        >
                          <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                            <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                              <Typography variant="body2" fontWeight={900}>
                                {test.testName || `Test ${testIdx + 1}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {test.testEntries?.length || 0} parameter(s){tr?.status ? ` • ${tr.status}` : ""}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={0.75}>
                              {(test.testEntries || []).map((entry, entryIdx) => {
                                const valueName = entry.valueName || entry.name || `Value ${entryIdx + 1}`;
                                const entryKey = `${testIdx}-${entryIdx}`;
                                const draft = testEntryValues[entryKey] || {};
                                const saved =
                                  savedEntries.find((se) => (se?.valueName || se?.name) === valueName) ||
                                  savedEntries[entryIdx] ||
                                  {};
                                const images = asArray(entry?.images?.data || entry?.images || saved?.images?.data || saved?.images);
                                const isPassFail = String(entry.resultType || "").toLowerCase() === "pass_fail";
                                const resultStatus = String(draft.resultStatus || "").toLowerCase();
                                const replacementSource = String(draft.replacementSource || "new").toLowerCase();
                                const needsReplacement = isPassFail && ["fail", "replaced"].includes(resultStatus);
                                const materialDecision = getTestEntryMaterialDecision(test, testIdx, entry, entryIdx);
                                return (
                                  <Card key={String(valueName)} variant="outlined">
                                    <Box sx={{ p: 1.0 }}>
                                      <Typography variant="body2" fontWeight={900}>
                                        {valueName}
                                      </Typography>
                                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                        <Row label="Pass value" value={entry.passValue != null && entry.passValue !== "" ? String(entry.passValue) : "—"} />
                                        <Row label="Min" value={entry.minValue != null && entry.minValue !== "" ? String(entry.minValue) : "—"} />
                                        <Row label="Saved actual" value={saved?.actualValue != null && saved.actualValue !== "" ? String(saved.actualValue) : "—"} />
                                        <Row label="Saved after fix" value={saved?.afterFixValue != null && saved.afterFixValue !== "" ? String(saved.afterFixValue) : "—"} />
                                        <Row label="Saved status" value={saved?.resultStatus || saved?.status || "—"} />
                                        <Row label="Repair" value={saved?.repairAction || saved?.repair || "—"} />
                                      </Stack>
                                      <Stack spacing={0.75} sx={{ mt: 1 }}>
                                        {isPassFail ? (
                                          <>
                                            <FormControl fullWidth size="small" disabled={!stageRecordData}>
                                              <Select
                                                value={draft.resultStatus || ""}
                                                displayEmpty
                                                onChange={(event) =>
                                                  setTestEntryValues((prev) => ({
                                                    ...prev,
                                                    [entryKey]: {
                                                      ...(prev[entryKey] || {}),
                                                      resultStatus: event.target.value,
                                                      ...(event.target.value === "pass" ? { replacementSource: "", replacementNote: "", partsBankLineKey: "", partsBankPartName: "" } : {}),
                                                      ...(event.target.value === "fail" && !(prev[entryKey] || {}).replacementSource ? { replacementSource: "new" } : {}),
                                                      ...(event.target.value === "replaced" && !(prev[entryKey] || {}).replacementSource ? { replacementSource: "new" } : {}),
                                                    },
                                                  }))
                                                }
                                              >
                                                <MenuItem value="">Select result</MenuItem>
                                                <MenuItem value="pass">{entry.passLabel || "Pass"}</MenuItem>
                                                <MenuItem value="fail">{entry.failLabel || "Fail / damaged"}</MenuItem>
                                                <MenuItem value="replaced">Replaced</MenuItem>
                                              </Select>
                                            </FormControl>
                                            {needsReplacement ? (
                                              <>
                                                <FormControl fullWidth size="small" disabled={!stageRecordData}>
                                                  <Select
                                                    value={draft.replacementSource || "new"}
                                                    onChange={(event) =>
                                                      setTestEntryValues((prev) => ({
                                                        ...prev,
                                                        [entryKey]: { ...(prev[entryKey] || {}), replacementSource: event.target.value },
                                                      }))
                                                    }
                                                  >
                                                    <MenuItem value="new">New item</MenuItem>
                                                    <MenuItem value="bank">Parts bank</MenuItem>
                                                  </Select>
                                                </FormControl>
                                                {replacementSource === "bank" ? (
                                                  <FormControl fullWidth size="small" disabled={!stageRecordData || !availablePartsBankLines.length}>
                                                    <Select
                                                      value={draft.partsBankLineKey || ""}
                                                      displayEmpty
                                                      onChange={(event) => {
                                                        const selectedLine = availablePartsBankLines.find((row) => String(row?.lineKey || "") === String(event.target.value));
                                                        setTestEntryValues((prev) => ({
                                                          ...prev,
                                                          [entryKey]: {
                                                            ...(prev[entryKey] || {}),
                                                            partsBankLineKey: event.target.value,
                                                            partsBankPartName: selectedLine?.partName || "",
                                                            replacementNote: "",
                                                          },
                                                        }));
                                                      }}
                                                    >
                                                      <MenuItem value="">Select parts bank item</MenuItem>
                                                      {availablePartsBankLines.map((row) => {
                                                        const lineKey = String(row?.lineKey || "");
                                                        const remaining = Number(partsBankAvailByKey?.[lineKey]?.qtyRemaining ?? 0) || 0;
                                                        return (
                                                          <MenuItem key={lineKey} value={lineKey}>
                                                            {row?.partName || lineKey}{row?.group ? ` - ${row.group}` : ""} ({remaining} available)
                                                          </MenuItem>
                                                        );
                                                      })}
                                                    </Select>
                                                  </FormControl>
                                                ) : (
                                                  <TextField
                                                    label="Repair / replacement note"
                                                    value={draft.replacementNote || ""}
                                                    onChange={(event) => setTestEntryValues((prev) => ({ ...prev, [entryKey]: { ...(prev[entryKey] || {}), replacementNote: event.target.value } }))}
                                                    disabled={!stageRecordData}
                                                    size="small"
                                                  />
                                                )}
                                              </>
                                            ) : null}
                                          </>
                                        ) : (
                                          <>
                                            <TextField
                                              label="Actual value"
                                              value={draft.actualValue ?? ""}
                                              onChange={(event) => setTestEntryValues((prev) => ({ ...prev, [entryKey]: { ...(prev[entryKey] || {}), actualValue: event.target.value } }))}
                                              disabled={!stageRecordData}
                                              size="small"
                                              inputProps={{ inputMode: "decimal" }}
                                            />
                                            <TextField
                                              label="After fix value"
                                              value={draft.afterFixValue ?? ""}
                                              onChange={(event) => setTestEntryValues((prev) => ({ ...prev, [entryKey]: { ...(prev[entryKey] || {}), afterFixValue: event.target.value } }))}
                                              disabled={!stageRecordData}
                                              size="small"
                                              inputProps={{ inputMode: "decimal" }}
                                            />
                                          </>
                                        )}
                                      </Stack>
                                      <Box sx={{ mt: 1, p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#F8FAFC" }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: materialDecision.hasRules || replacementSource === "bank" ? 0.75 : 0 }}>
                                          <Icon sx={{ fontSize: 18, color: iosColors.orange }}>precision_manufacturing</Icon>
                                          <Typography sx={{ fontSize: 12, fontWeight: 900, color: iosColors.ink, textTransform: "uppercase" }}>
                                            Material decision
                                          </Typography>
                                        </Stack>
                                        {isPassFail && needsReplacement && replacementSource === "bank" ? (
                                          <Chip
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            label={draft.partsBankPartName ? `Parts bank: ${draft.partsBankPartName}` : "Parts bank selected"}
                                            sx={{ fontWeight: 850 }}
                                          />
                                        ) : !materialDecision.hasRules ? (
                                          <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>No conditional material rule on this value.</Typography>
                                        ) : materialDecision.pending ? (
                                          <Chip size="small" variant="outlined" label="Enter result to calculate" sx={{ fontWeight: 850 }} />
                                        ) : materialDecision.matched.length ? (
                                          <Stack spacing={0.5}>
                                            {materialDecision.matched.map((item) => (
                                              <Box key={`${item.inventoryItemId}-${item.ruleId || item.reason || ""}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                                <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                                                  <Typography sx={{ color: iosColors.ink, fontSize: 13.5, fontWeight: 900 }}>
                                                    {item.sku || "Replacement part"}
                                                  </Typography>
                                                  <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>
                                                    {item.name || item.reason || "Test-rule material"}
                                                  </Typography>
                                                </Box>
                                                <Chip size="small" label={`Qty ${Number(item.quantity || 0)}`} sx={{ fontWeight: 900, flexShrink: 0 }} />
                                              </Box>
                                            ))}
                                          </Stack>
                                        ) : (
                                          <Chip size="small" color="success" variant="outlined" label="No conditional part needed" sx={{ fontWeight: 850 }} />
                                        )}
                                      </Box>
                                      {images.length ? (
                                        <Box sx={{ mt: 1 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                            Images
                                          </Typography>
                                          <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.25 }}>
                                            {images.map((img, imgIdx) => {
                                              const f = resolveFileFromAny(img);
                                              const src = getFileUrl(f, baseURL);
                                              if (!src) return null;
                                              return (
                                                <Box
                                                  key={String(getMediaId(img) || imgIdx)}
                                                  component="img"
                                                  src={src}
                                                  alt={getFileName(f) || `Image ${imgIdx + 1}`}
                                                  sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 12, border: "1px solid", borderColor: "divider" }}
                                                  onClick={() => openViewer({ file: f, title: getFileName(f) || `Image ${imgIdx + 1}`, annotations: [] })}
                                                />
                                              );
                                            })}
                                          </Box>
                                        </Box>
                                      ) : null}
                                    </Box>
                                  </Card>
                                );
                              })}
                            </Stack>
                            <Button
                              variant="contained"
                              disabled={!stageRecordData || saveTestMutation.isPending}
                              onClick={() => saveTestMutation.mutate({ test, testIdx })}
                              sx={{ ...primaryActionSx, mt: 1 }}
                              startIcon={<Icon>save</Icon>}
                            >
                              {saveTestMutation.isPending ? "Saving..." : "Save Test"}
                            </Button>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                  {!stageRecordData ? (
                    <StickyActionFooter>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Icon>play_circle</Icon>}
                        disabled={startBuildMutation.isPending}
                        onClick={() => startBuildMutation.mutate()}
                        sx={primaryActionSx}
                      >
                        {startBuildMutation.isPending ? "Starting..." : "Start Build"}
                      </Button>
                    </StickyActionFooter>
                  ) : null}
                </Stack>
              ) : null}

              {tab === 3 ? (
                <Stack spacing={0.75} sx={{ pb: normalizedQualityChecks.length ? 9.5 : 0 }}>
                  {normalizedQualityChecks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No quality checks defined on build sheet.
                    </Typography>
                  ) : (
                    <>
                      {normalizedQualityChecks.map((qc, idx) => {
                        const checkName = qc.title || qc.name || `QC ${idx + 1}`;
                        const localKey = `${idx}::${checkName}`;
                        const localValue = qualityCheckValues[localKey] || { notes: "", images: [] };
                        const uploadedImages = localValue.images || [];
                        return (
                          <Accordion
                            key={String(qc.id || idx)}
                            disableGutters
                            elevation={0}
                            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", "&:before": { display: "none" } }}
                          >
                            <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                              <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                                <Typography variant="body2" fontWeight={900}>
                                  {checkName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {qc.images?.length ? `${qc.images.length} reference` : "No reference"} • {uploadedImages.length} uploaded
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={0.85}>
                                {qc.body ? (
                                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", "& p": { margin: 0 } }} dangerouslySetInnerHTML={{ __html: qc.body }} />
                                ) : null}
                                {Array.isArray(qc.images) && qc.images.length ? (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                      Reference images
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.25 }}>
                                      {qc.images.map((img, imgIdx) => {
                                        const f = resolveFileFromAny(img);
                                        const src = getFileUrl(f, baseURL);
                                        if (!src) return null;
                                        return (
                                          <Box
                                            key={String(getMediaId(img) || imgIdx)}
                                            component="img"
                                            src={src}
                                            alt={getFileName(f) || `Image ${imgIdx + 1}`}
                                            sx={{ width: 112, height: 112, objectFit: "cover", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                                            onClick={() => openViewer({ file: f, title: getFileName(f) || `Image ${imgIdx + 1}`, annotations: [] })}
                                          />
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                ) : null}
                                <TextField
                                  label="Technician notes"
                                  value={localValue.notes || ""}
                                  onChange={(event) => setQualityCheckValues((prev) => ({ ...prev, [localKey]: { ...(prev[localKey] || { images: [] }), notes: event.target.value } }))}
                                  multiline
                                  minRows={2}
                                  disabled={!stageRecordData}
                                />
                                <Button component="label" variant="outlined" disabled={!stageRecordData} sx={secondaryActionSx} startIcon={<Icon>add_photo_alternate</Icon>}>
                                  Upload QC Images
                                  <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={(event) => {
                                      uploadQualityCheckImages(idx, checkName, event.target.files);
                                      event.target.value = null;
                                    }}
                                  />
                                </Button>
                                {uploadedImages.length ? (
                                  <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.25 }}>
                                    {uploadedImages.map((img, imgIdx) => {
                                      const f = resolveFileFromAny(img);
                                      const src = getFileUrl(f, baseURL);
                                      if (!src) return null;
                                      return (
                                        <Box
                                          key={String(getMediaId(img) || imgIdx)}
                                          component="img"
                                          src={src}
                                          alt={getFileName(f) || `Uploaded ${imgIdx + 1}`}
                                          sx={{ width: 86, height: 86, objectFit: "cover", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                                          onClick={() => openViewer({ file: f, title: getFileName(f) || `Uploaded ${imgIdx + 1}`, annotations: [] })}
                                        />
                                      );
                                    })}
                                  </Box>
                                ) : null}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </>
                  )}
                  {normalizedQualityChecks.length ? (
                    <StickyActionFooter>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={!stageRecordData || saveQualityCheckValuesMutation.isPending}
                        onClick={() => saveQualityCheckValuesMutation.mutate()}
                        sx={primaryActionSx}
                        startIcon={<Icon>save</Icon>}
                      >
                        {saveQualityCheckValuesMutation.isPending ? "Saving..." : "Save Quality Checks"}
                      </Button>
                    </StickyActionFooter>
                  ) : null}
                </Stack>
              ) : null}

              {tab === 7 ? (
                <Stack spacing={1} sx={{ pb: 9.5 }}>
                  <IosCard>
                    <Stack spacing={0.85}>
                      <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
                        Component demand
                      </Typography>
                      {actionNotice ? (
                        <Typography sx={{ color: actionNotice.type === "success" ? iosColors.green : iosColors.red, fontSize: 12.5, fontWeight: 850 }}>
                          {actionNotice.text}
                        </Typography>
                      ) : null}
                      {componentDemandPreviewError ? (
                        <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 850 }}>
                          {componentDemandPreviewError?.response?.data?.error?.message || componentDemandPreviewError?.message || "Unable to calculate component demand."}
                        </Typography>
                      ) : null}
                      {componentDemandPreview?.existingOpenOrder ? (
                        <Chip size="small" color="warning" variant="outlined" label={`Open order: ${componentDemandPreview.existingOpenOrder.orderNumber || componentDemandPreview.existingOpenOrder.documentId || componentDemandPreview.existingOpenOrder.id}`} sx={{ fontWeight: 900, alignSelf: "flex-start" }} />
                      ) : null}
                      {componentDemandPreviewLoading ? <LinearProgress /> : null}
                      {liveConditionalMaterialDemand.pending.length ? (
                        <Box sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#FFF7ED" }}>
                          <Typography sx={{ color: iosColors.orange, fontSize: 12.5, fontWeight: 850 }}>
                            Some test-rule materials are waiting on test values.
                          </Typography>
                        </Box>
                      ) : null}
                      <Stack spacing={0.65}>
                        {displayedComponentDemandLines.map((row, idx) => {
                          const firstSource = Array.isArray(row.sources) ? row.sources[0] : null;
                          const source = firstSource?.source === "test_rule" ? "Test rule" : "Always used";
                          const detail = [firstSource?.testName, firstSource?.testEntryName, firstSource?.reason].filter(Boolean).join(" - ");
                          return (
                            <Box key={`${row.inventoryItemId || idx}-${idx}`} sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                                <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                                  <Typography sx={{ color: iosColors.ink, fontSize: 14.5, fontWeight: 900 }}>
                                    {row.sku || "Component"}
                                  </Typography>
                                  <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.25 }}>
                                    {row.name || "-"}
                                  </Typography>
                                  <Typography sx={{ color: iosColors.orange, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase", mt: 0.45 }}>
                                    {source}{detail ? ` • ${detail}` : ""}
                                  </Typography>
                                </Box>
                                <Chip size="small" variant="outlined" label={`Qty ${row.qtyRequested || 0}`} sx={{ fontWeight: 900, flexShrink: 0 }} />
                              </Stack>
                            </Box>
                          );
                        })}
                        {!displayedComponentDemandLines.length && !componentDemandPreviewLoading ? (
                          <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No additional components are needed for this valve body.</Typography>
                        ) : null}
                      </Stack>
                    </Stack>
                  </IosCard>

                  <IosCard>
                    <Stack spacing={0.85}>
                      <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
                        Materials fulfilled
                      </Typography>
                      {componentMissingFulfilledLines.length ? (
                        <Stack spacing={0.45}>
                          {componentMissingFulfilledLines.map((row) => (
                            <Stack key={row.inventoryItemId} direction="row" spacing={0.75} alignItems="center">
                              <Icon sx={{ color: iosColors.orange, fontSize: 18 }}>radio_button_unchecked</Icon>
                              <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>
                                {row.sku || row.name || row.inventoryItemId}: {row.qtyFulfilled}/{row.qtyNeeded}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Icon sx={{ color: iosColors.green, fontSize: 20 }}>check_circle</Icon>
                          <Typography sx={{ color: iosColors.green, fontSize: 13, fontWeight: 850 }}>Component materials are fulfilled.</Typography>
                        </Stack>
                      )}
                    </Stack>
                  </IosCard>
                  <StickyActionFooter>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Icon>inventory</Icon>}
                      disabled={componentOrderMutation.isPending || componentDemandPreviewLoading || !displayedComponentDemandLines.length}
                      onClick={() => componentOrderMutation.mutate()}
                      sx={primaryActionSx}
                    >
                      {componentOrderMutation.isPending ? "Creating..." : "Create Component Order"}
                    </Button>
                  </StickyActionFooter>

                </Stack>
              ) : null}

              {tab === 8 ? (
                <Stack spacing={1} sx={{ pb: 9.5 }}>
                  <IosCard>
                    <Stack spacing={0.85}>
                      <Typography sx={{ color: iosColors.green, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
                        Parts bank
                      </Typography>
                      {actionNotice ? (
                        <Typography sx={{ color: actionNotice.type === "success" ? iosColors.green : iosColors.red, fontSize: 12.5, fontWeight: 850 }}>
                          {actionNotice.text}
                        </Typography>
                      ) : null}
                      <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>
                        {selectedTemplate?.name || (coreItemTemplateRefKey ? "No matching parts bank template" : "No core item mapping for this build sheet")}
                      </Typography>
                      <TextField
                        label="Search parts bank"
                        value={partsBankSearch}
                        onChange={(e) => setPartsBankSearch(e.target.value)}
                        size="small"
                      />
                      <Stack spacing={0.75}>
                        {partsBankTemplateLines.map((row, idx) => {
                          const key = String(row?.lineKey || "").trim();
                          const remaining = Number(partsBankAvailByKey?.[key]?.qtyRemaining ?? 0) || 0;
                          const imgs = asArray(row?.referenceImages?.data || row?.referenceImages);
                          const hero = imgs[0] || null;
                          const heroFile = hero ? resolveFileFromAny(hero) : null;
                          const heroUrl = heroFile ? getFileUrl(heroFile, baseURL) : null;
                          return (
                            <Box key={key || idx} sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                              <Stack spacing={0.75}>
                                <Stack direction="row" spacing={0.85} alignItems="flex-start">
                                  <Box
                                    sx={{ width: 70, height: 70, borderRadius: 1, border: `1px solid ${iosColors.line}`, bgcolor: iosColors.canvas, overflow: "hidden", display: "grid", placeItems: "center", flexShrink: 0 }}
                                    onClick={() => heroFile && openViewer({ file: heroFile, title: row?.partName || "Reference", annotations: [] })}
                                  >
                                    {heroUrl ? (
                                      <Box component="img" src={heroUrl} alt={row?.partName || "Reference"} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : (
                                      <Icon sx={{ color: iosColors.subtle }}>image</Icon>
                                    )}
                                  </Box>
                                  <Box sx={{ minWidth: 0, flex: 1, ...visibleTextSx }}>
                                    <Typography sx={{ color: iosColors.ink, fontSize: 14.5, fontWeight: 900 }}>
                                      {row?.partName || "(unnamed)"}
                                    </Typography>
                                    <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>{row?.group || "No group"}</Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.55 }}>
                                      <Chip size="small" variant="outlined" label={`Remaining ${remaining}`} sx={{ fontWeight: 850 }} />
                                      {imgs.length > 1 ? <Chip size="small" variant="outlined" label={`${imgs.length} images`} sx={{ fontWeight: 850 }} /> : null}
                                    </Stack>
                                  </Box>
                                </Stack>
                                {imgs.length > 1 ? (
                                  <Box sx={{ display: "flex", gap: 0.6, overflowX: "auto", pb: 0.25 }}>
                                    {imgs.map((img, imgIdx) => {
                                      const file = resolveFileFromAny(img);
                                      const url = getFileUrl(file, baseURL);
                                      if (!url) return null;
                                      return (
                                        <Box
                                          key={String(getMediaId(img) || imgIdx)}
                                          component="img"
                                          src={url}
                                          alt={getFileName(file) || `Reference ${imgIdx + 1}`}
                                          sx={{ width: 44, height: 44, objectFit: "cover", borderRadius: 0.75, border: `1px solid ${iosColors.line}` }}
                                          onClick={() => openViewer({ file, title: row?.partName || "Reference", annotations: [] })}
                                        />
                                      );
                                    })}
                                  </Box>
                                ) : null}
                                <TextField
                                  label="Request qty"
                                  value={partsBankQtyByLineKey?.[key] ?? ""}
                                  onChange={(e) => setPartsBankQtyByLineKey((prev) => ({ ...(prev || {}), [key]: e.target.value }))}
                                  size="small"
                                  inputProps={{ inputMode: "decimal" }}
                                />
                              </Stack>
                            </Box>
                          );
                        })}
                        {!partsBankTemplateLines.length ? (
                          <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>
                            No available parts bank lines for this unit’s core.
                          </Typography>
                        ) : null}
                      </Stack>
                    </Stack>
                  </IosCard>

                  {unitUsages.length ? (
                    <IosCard>
                      <Stack spacing={0.85}>
                        <Typography sx={{ color: iosColors.green, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
                          Parts bank usage
                        </Typography>
                        {unitUsages.slice(0, 20).map((usage) => {
                          const linesUsed = Array.isArray(usage?.linesUsed) ? usage.linesUsed : [];
                          return linesUsed.map((lineUsed, idx) => (
                            <Stack key={`${usage.documentId || usage.id}-${idx}`} direction="row" justifyContent="space-between" spacing={1}>
                              <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>
                                {fmtDateTime(usage.usedAt)} • {lineUsed?.lineKey || "Part"}
                              </Typography>
                              <Typography sx={{ color: iosColors.ink, fontSize: 12.5, fontWeight: 900 }}>{lineUsed?.qtyUsed || 0}</Typography>
                            </Stack>
                          ));
                        })}
                      </Stack>
                    </IosCard>
                  ) : null}
                  <StickyActionFooter>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Icon>shopping_cart</Icon>}
                      disabled={partsBankRequestMutation.isPending || !selectedTemplate || !partsBankTemplateLines.length}
                      onClick={() => partsBankRequestMutation.mutate()}
                      sx={primaryActionSx}
                    >
                      {partsBankRequestMutation.isPending ? "Creating..." : "Create Parts Bank Order"}
                    </Button>
                  </StickyActionFooter>
                </Stack>
              ) : null}

              {tab === 4 ? (
                <Stack spacing={0.75}>
                  {normalizedAdditionalInfo.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No additional information.
                    </Typography>
                  ) : (
                    normalizedAdditionalInfo.map((ai, idx) => (
                      <Accordion
                        key={String(ai.name || idx)}
                        disableGutters
                        elevation={0}
                        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", "&:before": { display: "none" } }}
                      >
                        <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                          <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                            <Typography variant="body2" fontWeight={900}>
                              {ai.name || `Info ${idx + 1}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ai.documents?.length ? `${ai.documents.length} document(s)` : "No documents"}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {Array.isArray(ai.documents) && ai.documents.length ? (
                            <Stack spacing={0.5}>
                              {ai.documents.map((doc, docIdx) => {
                                const f = resolveFileFromAny(doc);
                                const url = getFileUrl(f, baseURL);
                                if (!url) return null;
                                return (
                                  <Card
                                    key={String(getMediaId(doc) || docIdx)}
                                    variant="outlined"
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => openViewer({ file: f, title: getFileName(f) || `Document ${docIdx + 1}`, annotations: [] })}
                                  >
                                    <Box sx={{ p: 1.0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                      <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                                        <Typography variant="body2" fontWeight={900}>{getFileName(f) || `Document ${docIdx + 1}`}</Typography>
                                        <Typography variant="caption" color="text.secondary">{getFileType(f) || "file"}</Typography>
                                      </Box>
                                      <Icon fontSize="small">open_in_new</Icon>
                                    </Box>
                                  </Card>
                                );
                              })}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No documents.
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Stack>
              ) : null}

              {tab === 5 ? (
                <Stack spacing={0.75}>
                  {normalizedIdentifiers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No identifiers defined on build sheet.
                    </Typography>
                  ) : (
                    normalizedIdentifiers.map((ident, idx) => (
                      <Accordion
                        key={String(ident.name || idx)}
                        disableGutters
                        elevation={0}
                        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", "&:before": { display: "none" } }}
                      >
                        <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                          <Box sx={{ minWidth: 0, ...visibleTextSx }}>
                            <Typography variant="body2" fontWeight={900}>
                              {ident.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ident.required ? "Required" : "Optional"} • {ident.entries?.length || 0} field(s)
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {ident.description ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {ident.description}
                            </Typography>
                          ) : null}
                          <Stack spacing={0.75}>
                            {(ident.entries || []).map((entry, eIdx) => {
                              const key = `${ident.name}::${entry.name}`;
                              const v = identifierValuesMap?.[key] || {};
                              const imgs = asArray(v?.images?.data || v?.images);
                              return (
                                <Card key={String(entry.name || eIdx)} variant="outlined">
                                  <Box sx={{ p: 1.0 }}>
                                    <Typography variant="body2" fontWeight={900}>
                                      {entry.name}
                                    </Typography>
                                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                      <Row label="Data" value={v?.data || "—"} />
                                      <Row label="Change" value={v?.change || "—"} />
                                    </Stack>
                                    {imgs.length ? (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                          Images
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.25 }}>
                                          {imgs.map((img, imgIdx) => {
                                            const f = resolveFileFromAny(img);
                                            const src = getFileUrl(f, baseURL);
                                            if (!src) return null;
                                            return (
                                              <Box
                                                key={String(getMediaId(img) || imgIdx)}
                                                component="img"
                                                src={src}
                                                alt={getFileName(f) || `Image ${imgIdx + 1}`}
                                                sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 12, border: "1px solid", borderColor: "divider" }}
                                                onClick={() => openViewer({ file: f, title: getFileName(f) || `Image ${imgIdx + 1}`, annotations: [] })}
                                              />
                                            );
                                          })}
                                        </Box>
                                      </Box>
                                    ) : null}
                                  </Box>
                                </Card>
                              );
                            })}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Stack>
              ) : null}

        </Stack>
      ) : null}

      <MobileImageViewer
        open={viewer.open}
        onClose={() => setViewer({ open: false, file: null, title: "", annotations: [] })}
        file={viewer.file}
        baseURL={baseURL}
        title={viewer.title}
        annotations={viewer.annotations}
      />
    </IosPage>
  );
}

function Row({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
      <Typography variant="caption" color="text.secondary" sx={{ ...visibleTextSx, flexShrink: 0, maxWidth: "42%" }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={900} sx={{ ...visibleTextSx, minWidth: 0, flex: 1, textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function LinkRow({ label, onClick, disabled }) {
  return (
    <Card variant="outlined" sx={{ opacity: disabled ? 0.5 : 1 }}>
      <Box
        onClick={() => {
          if (disabled) return;
          onClick();
        }}
        sx={{
          p: 1.0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <Typography variant="body2" fontWeight={900}>
          {label}
        </Typography>
        <Icon fontSize="small">chevron_right</Icon>
      </Box>
    </Card>
  );
}

function StickyActionFooter({ children }) {
  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        bgcolor: "rgba(247,248,250,0.96)",
        borderTop: `1px solid ${iosColors.line}`,
        px: 1.25,
        pt: 0.85,
        pb: "calc(0.85rem + env(safe-area-inset-bottom))",
        backdropFilter: "blur(10px)",
      }}
    >
      {children}
    </Box>
  );
}

function ActionShortcut({ icon, label, onClick, tone, disabled }) {
  const color = tone === "red" ? iosColors.red : tone === "green" ? iosColors.green : iosColors.orange;
  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      sx={{
        minHeight: 76,
        border: `1px solid ${disabled ? "rgba(15,15,18,0.08)" : `${color}44`}`,
        borderRadius: 1,
        bgcolor: disabled ? "rgba(15,15,18,0.03)" : `${color}0F`,
        color,
        display: "flex",
        flexDirection: "column",
        gap: 0.45,
        alignItems: "center",
        justifyContent: "center",
        px: 0.5,
        opacity: disabled ? 0.48 : 1,
        textAlign: "center",
      }}
    >
      <Icon sx={{ fontSize: 23 }}>{icon}</Icon>
      <Typography sx={{ color: disabled ? iosColors.subtle : iosColors.ink, fontSize: 11.5, lineHeight: 1.05, fontWeight: 900 }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

Row.defaultProps = { value: "—" };

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

LinkRow.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

LinkRow.defaultProps = {
  disabled: false,
};

StickyActionFooter.propTypes = {
  children: PropTypes.node.isRequired,
};

ActionShortcut.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  tone: PropTypes.oneOf(["orange", "green", "red"]),
  disabled: PropTypes.bool,
};

ActionShortcut.defaultProps = {
  tone: "orange",
  disabled: false,
};
